import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PiUsersThreeLight, PiUserLight, PiXLight, PiStar, PiStarFill, PiSpinnerLight } from "react-icons/pi";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import Dropdown, { DropdownOption } from "./TailwindComponents/Dropdown";
import { FIELD_LABEL_CLASS, FIELD_HELP_CLASS, FIELD_GAP } from "./TailwindComponents/FieldShell";
import Button from "./TailwindComponents/Button";
import { ShareUser } from "./ShareAccessManager";
import ProfileImage from "../Header/ProfileImage";
import { fetchAllEmployees } from "../../store/actions/organization/employeeActions";
import Badge from "./TailwindComponents/Badge";
import { normalise } from './access/accessModel';
import Skeleton from './TailwindComponents/Skeleton';

// Employee type from org-people API response
interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  user_id?: number;
}

export interface Collaborator {
  user_id?: string | number;  // Legacy numeric ID
  collaborator_uuid?: string;  // New: UUID from org-people API
  access: 'edit' | 'view' | 'admin';
  is_lead?: boolean;  // New: indicates if this collaborator is the lead
  // Display fields - populated from backend enriched responses
  name?: string;
  email?: string;
  initials?: string;
  profile_picture?: string | null;
  // Provenance tracking - added as part of the access-model alignment
  // (plan: sharded-frolicking-lake.md). Populated by the backend on reads
  // and preserved on writes so cascade-revoke can find the right rows.
  //  - 'direct'   : explicit share, lives forever until removed directly
  //  - 'project'  : granted via Project membership; auto-revoked on cascade
  //  - 'team'/'department'/'org' : reserved for future inheritance sources
  source?: 'direct' | 'project' | 'team' | 'department' | 'org';
  granted_via_uuid?: string;
  // Who added this collaborator and when (from CollaboratorService enriched response)
  shared_by_user?: { name?: string; full_name?: string; email?: string; profile_picture?: string | null };
  shared_at?: string;
}

/**
 * A person's display name, WITHOUT ever emitting the string "undefined".
 *
 * React renders nothing for an undefined child, so a literal "undefined" on
 * screen can only come from a template literal - and this file had four of
 * them interpolating `first_name`/`last_name` directly. A person with only
 * one of the two names (external contacts routinely have only a first name)
 * rendered as "Ana undefined"; one with neither rendered "undefined
 * undefined", which is what showed in the task detail's Collaborators
 * section. Email is a better answer than half a name.
 */
export const personName = (person: any): string =>
  /*
    `name` FIRST. The collaborator endpoints return an already-composed full
    name and no `first_name`/`last_name` at all (those exist only nested under
    `shared_by_user`), so deriving from the name parts produced nothing and
    the row fell through to a blank. The org-people endpoint is the opposite
    - parts, no `name` - hence both, in this order.
  */
  person?.name
  || person?.full_name
  || [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim()
  || person?.email
  || '';

/** Up to two letters, from whichever name parts exist. Never "UN". */
export const personInitials = (person: any): string =>
  // Same order as `personName`: the server sends "SM" ready-made.
  (String(person?.initials || '')
   || [person?.first_name, person?.last_name]
      .filter(Boolean)
      .map((part: string) => String(part).charAt(0))
      .join('')
   || String(personName(person) || person?.email || '').charAt(0)
  ).toUpperCase();

interface OptionWithPerson extends DropdownOption {
  person?: any;
}

export interface CollaboratorSelectorProps {
  collaborators: Collaborator[];
  onCollaboratorsChange: (collaborators: Collaborator[]) => void;
  initialShared?: ShareUser[]; // Collaborators that should be pre-selected (e.g., parent folder collaborators)
  allowedPermissions?: ('edit' | 'view' | 'admin')[]; // Permissions to filter by
  title?: string;
  description?: string;
  ownerLabel?: string; // Custom label for owner (default: 'You are the lead attorney')
  showOwnerSection?: boolean; // Whether to show the owner/lead section (default: true)
  enableLeadSelection?: boolean; // New: Enable lead selection functionality (default: false)
  adminUuid?: string; // UUID of the admin/creator who cannot be removed or have access changed
  excludeUserIds?: string[]; // UUIDs of users to exclude from the dropdown (e.g., existing members)
  // Optional - callers pass a resolver so the "Inherited from Project X"
  // pill can show the container's actual name instead of a generic label.
  // Receives (source, granted_via_uuid) and returns a human-readable string
  // or null if unknown. If omitted, the pill falls back to "Inherited".
  resolveSourceLabel?: (source: Collaborator['source'], granted_via_uuid?: string) => string | null;
  hideSelectedList?: boolean; // When true, hides the "Added Collaborators" list below the search - use when the parent renders its own collaborator list
  /**
   * View-only: the list renders without the access dropdown, the remove
   * control or the add-people search. For callers whose current user may see
   * who has access but is not permitted to change it.
   */
  readOnly?: boolean;
  /** Prompt on the add-person control. Defaults to a generic one. */
  addPlaceholder?: string;
}

const CollaboratorSelector: React.FC<CollaboratorSelectorProps> = ({
  collaborators,
  onCollaboratorsChange,
  initialShared = [],
  allowedPermissions = ['edit', 'view'],
  title,
  description,
  ownerLabel,
  showOwnerSection = true,
  enableLeadSelection = false, // Default: false
  adminUuid = undefined,
  excludeUserIds = [],
  resolveSourceLabel,
  hideSelectedList = false,
  readOnly = false,
  addPlaceholder,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.user?.userInfo);
  const [people, setPeople] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentUserIsLead, setCurrentUserIsLead] = useState(enableLeadSelection); // Only default to true if lead selection is enabled

  // Fetch people on mount
  useEffect(() => {
    setLoading(true);
    (dispatch as any)(fetchAllEmployees({ limit: 100, page: 1 }))
      .then((response: any) => {
        const peopleArray = response?.employees || [];
        setPeople(peopleArray);
        setLoading(false);
      })
      .catch((error: any) => {
        console.error('[CollaboratorSelector] Error fetching people:', error);
        setLoading(false);
      });
  }, [dispatch]);

  // Get current user ID to filter them out
  const currentUserId = userInfo?.UserUID;

  // Custom option component for rich dropdown display
  const OptionWithAvatar = ({ person }: { person: any }) => (
    <div className="flex items-center gap-3 py-1">
      <ProfileImage
        profilePicture={normalise(person).avatar}
        email={person.email}
        initials={personInitials(person)}
        firstName={person.first_name}
        lastName={person.last_name}
        size="h-8 w-8"
        className="flex-shrink-0"
      />
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {personName(person)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {person.email}
        </span>
      </div>
    </div>
  );

  // Build dropdown options from people
  const dropdownOptions: OptionWithPerson[] = useMemo(() => {
    return people
      .filter((person: any) => {
        // Only include active users, exclude current user and explicitly excluded users
        return person.is_active && person.id !== currentUserId && !excludeUserIds.includes(person.id);
      })
      .map((person: any) => ({
        value: person.id,  // UUID
        label: personName(person),
        person  // Store the person object for custom rendering
      }));
  }, [people, currentUserId, excludeUserIds]);

  // Get already selected IDs
  const selectedIds = useMemo(() => {
    return collaborators.map(c => c.collaborator_uuid || c.user_id).filter(Boolean).map(String);
  }, [collaborators]);

  // Available options (not yet selected)
  const availableOptions = useMemo(() => {
    return dropdownOptions.filter(opt => !selectedIds.includes(String(opt.value)));
  }, [dropdownOptions, selectedIds]);

  /**
   * Pick several people, then commit.
   *
   * The add control used to be a single-select whose `onChange` added a person
   * immediately: the menu closed, the list re-rendered and the caller reloaded
   * on every click, so adding four people meant four open-select-wait cycles.
   * Selections now accumulate in the menu (checkboxes, menu stays open) and
   * land in one go on Add.
   */
  const [pendingIds, setPendingIds] = useState<string[]>([]);

  const toCollaborator = (personId: string): Collaborator => {
    const person = people.find(p => p.id === personId);
    return {
      collaborator_uuid: personId, // UUID for backend - primary identifier
      user_id: person?.user_id,
      access: 'edit',
      is_lead: enableLeadSelection ? false : undefined, // Only include is_lead when enabled
      // Populate display fields from the person record so the parent can render immediately
      name: personName(person) || undefined,
      email: person?.email,
      initials: person ? personInitials(person) || undefined : undefined,
      // Through `accessModel`, so the picture survives whichever spelling the
      // directory row used (`avatar_url` / `avatarUrl` / `profile_picture` / …)
      // rather than only the two this line used to check.
      profile_picture: normalise(person).avatar,
    };
  };

  // Handle adding the pending selection
  const handleAddPending = () => {
    if (pendingIds.length === 0) return;
    const additions = pendingIds
      .filter(id => !selectedIds.includes(String(id)))
      .map(toCollaborator);
    setPendingIds([]);
    if (additions.length > 0) {
      onCollaboratorsChange([...collaborators, ...additions]);
    }
  };

  // Handle removing collaborator
  const handleRemove = (personId: string) => {
    // Prevent removing admin
    if (personId === adminUuid) return;
    
    const collabToRemove = collaborators.find(c => (c.collaborator_uuid || String(c.user_id)) === personId);
    const filtered = collaborators.filter(c => {
      const id = c.collaborator_uuid || String(c.user_id);
      return id !== personId;
    });
    onCollaboratorsChange(filtered);
    // If the removed collaborator was lead, set current user as lead
    if (collabToRemove?.is_lead) {
      setCurrentUserIsLead(true);
    }
  };

  // Handle access change
  const handleAccessChange = (personId: string, access: 'edit' | 'view') => {
    // Prevent changing access for admin
    if (personId === adminUuid) return;
    
    const updated = collaborators.map(c => {
      const id = c.collaborator_uuid || String(c.user_id);
      return id === personId ? { ...c, access } : c;
    });
    onCollaboratorsChange(updated);
  };

  // Handle toggling lead for a collaborator
  const handleToggleLead = (personId: string) => {
    const updated = collaborators.map(c => {
      const id = c.collaborator_uuid || String(c.user_id);
      if (id === personId) {
        const newIsLead = !c.is_lead;
        if (newIsLead) {
          setCurrentUserIsLead(false); // If setting collaborator as lead, unset current user
        }
        return { ...c, is_lead: newIsLead };
      } else {
        return { ...c, is_lead: false }; // Ensure only one lead
      }
    });
    onCollaboratorsChange(updated);
  };

  // Handle toggling current user as lead
  const handleToggleCurrentUserLead = () => {
    const newCurrentUserIsLead = !currentUserIsLead;
    setCurrentUserIsLead(newCurrentUserIsLead);
    if (newCurrentUserIsLead) {
      // Unset lead from all collaborators
      const updated = collaborators.map(c => ({ ...c, is_lead: false }));
      onCollaboratorsChange(updated);
    }
  };
  
  const permissionOptions = useMemo(() => {
    return allowedPermissions.map(perm => ({
      value: perm,
      label: perm === 'edit' ? t('Edit') : t('View')
    }));
  }, [allowedPermissions, t]);

  // Get person info by ID
  const getPersonById = (personId: string) => {
    return people.find(p => p.id === personId);
  };

  // Get person info by ID
  const getPersonName = (personId: string) => {
    const person = getPersonById(personId);
    // Not in the fetched page of employees - the id is a poor label, but it
    // is at least true. `personId` is guaranteed non-"undefined" by the
    // caller below.
    if (!person) return personId;
    return personName(person) || personId;
  };

  return (
    <div className="rounded p-1">
      {/* Header - only when a title is explicitly provided, so it doesn't
          duplicate a section header that already labels this field (e.g. "Access"). */}
      {title && <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>{title}</p>}

      {description && (
        <p className={`${FIELD_HELP_CLASS} ${FIELD_GAP}`}>{description}</p>
      )}

      {/* Show loading state for entire section while people are loading */}
      {loading ? (
        <div className="py-2" role="status" aria-label={t('Loading people')}>
          <Skeleton variant="list" rows={3} inset="none" />
        </div>
      ) : (
        <>
          {/* Dropdown to add collaborators - not offered to a viewer who
              cannot change access anyway. */}
          <div className={`space-y-2${readOnly ? ' hidden' : ''}`}>
            {/* items-center: the Add button sat top-aligned beside the taller
                field (feedback round 3, Contracts share panel). */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
              <Dropdown
                options={availableOptions as OptionWithPerson[]}
                multiple
                value={pendingIds}
                onChange={(value) => {
                  const next = Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
                  setPendingIds(next);
                }}
                placeholder={availableOptions.length === 0
                  ? t('No more people to add')
                  : pendingIds.length > 0
                    ? `${pendingIds.length} ${t('selected')}`
                    : (addPlaceholder || t('Select people to collaborate'))}
                width="w-full"
                renderOption={(option: OptionWithPerson) => <OptionWithAvatar person={option.person} />}
              />
              </div>
              {/* Commit control: the menu keeps its selections until this is
                  pressed, so the caller reloads once instead of per click. */}
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={handleAddPending}
                disabled={pendingIds.length === 0}
              >
                {pendingIds.length > 1 ? `${t('Add')} (${pendingIds.length})` : t('Add')}
              </Button>
            </div>

            {/* List of added collaborators - hidden when parent renders its own list */}
            {!hideSelectedList && collaborators.length > 0 && (
              <div className="glass space-y-2 mt-3 p-3 rounded-lg">
                <p className="text-xs text-gray-700 dark:text-gray-300">
                  {t('Added collaborators')} ({collaborators.length})
                </p>
                <div className="space-y-2">
                  {collaborators.map((collab, idx) => {
                    // NOT `String(collab.user_id)` - a row missing both ids
                    // stringified to the literal "undefined", which then went
                    // through `getPersonName` unmatched and rendered as the
                    // person's name.
                    const personId = String(collab.collaborator_uuid || collab.user_id || '');
                    const person = getPersonById(personId);
                    /*
                      The ROW is the better source, and is checked first.

                      `people` is one page of org employees (limit 100) keyed
                      by the org-people id, so a collaborator outside that page
                      - or identified by a different id - never matched and the
                      row rendered blank. The collaborator responses already
                      carry name / initials / avatar / email, which is exactly
                      what this row needs; the directory lookup is now only a
                      fallback for rows that arrive as a bare uuid + access.
                    */
                    const name = personName(collab) || getPersonName(personId) || t('Unknown person');
                    const initials = personInitials(collab) || personInitials(person);
                    const avatar = collab.profile_picture
                      || (collab as any).avatar_url
                      || person?.profile_picture
                      || person?.ProfilePicture
                      || null;
                    const email = collab.email || person?.email || '';
                    const isAdmin = personId === adminUuid;
                    // Provenance detection - rows whose grant came from a
                    // container (Project / Team / ...) are rendered read-only
                    // because removing them here would be a no-op: the next
                    // cascade would re-add the row. The proper way to revoke
                    // inherited access is at the container level (unlink the
                    // resource from the Project, or remove the user from the
                    // Project).
                    const isInherited = !!collab.source && collab.source !== 'direct';
                    // Resolve a human-readable label; fall back to a generic
                    // "Inherited" if the caller didn't pass a resolver.
                    const sourceLabel = isInherited
                      ? (resolveSourceLabel?.(collab.source, collab.granted_via_uuid)
                          || (collab.source === 'project' ? t('Inherited from project')
                              : collab.source === 'team' ? t('Inherited from team')
                              : collab.source === 'department' ? t('Inherited from department')
                              : t('Inherited')))
                      : null;

                return (
                  <div
                    key={`${personId}-${idx}`}
                    className={`flex items-center justify-between gap-3 p-2 rounded border ${
                      isAdmin
                        ? 'bg-info-50 dark:bg-info-900/20 border-info-200 dark:border-info-800'
                        : isInherited
                        ? 'bg-warning-50/60 dark:bg-warning-900/10 border-warning-200 dark:border-warning-800/60'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {enableLeadSelection && (
                        <Button
                          type="button"
                          variant="tertiary"
                          size="small"
                          onClick={() => handleToggleLead(personId)}
                          ariaLabel={collab.is_lead ? t('Unset as lead') : t('Set as lead')}
                          className={`p-1 ${collab.is_lead ? 'text-info-500' : 'text-gray-400 hover:text-info-500'}`}
                        >
                          {collab.is_lead ? <PiStarFill className="h-4 w-4" /> : <PiStar className="h-4 w-4" />}
                        </Button>
                      )}
                      {/* Rendered whenever there is anything to render - it
                          used to be gated on the directory lookup, so a row
                          the directory did not know had no avatar at all. */}
                      {(initials || avatar || email) && (
                        <ProfileImage
                          profilePicture={avatar}
                          email={email}
                          initials={initials}
                          firstName={person?.first_name}
                          lastName={person?.last_name}
                          size="h-6 w-6"
                          className="flex-shrink-0"
                        />
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{name}</span>
                        {isInherited && (
                          <span
                            className="text-[10px] text-warning-700 dark:text-warning-400 truncate"
                            title={t('To remove this access, unlink the resource from the project or remove the member from the project.')}
                          >
                            {sourceLabel}
                          </span>
                        )}
                      </div>
                    </div>
                        {isAdmin && (
                          <Badge color="blue">
                            {t('Owner')}
                          </Badge>
                        )}
                      {isInherited && !isAdmin && (
                        // Read-only pill instead of the access dropdown - a user
                        // with inherited access can't have their level silently
                        // changed here without creating a second (direct) row
                        // that would confuse provenance. Make the intent explicit.
                        <Badge color="yellow">
                          {collab.access === 'admin' ? t('Admin') : collab.access === 'edit' ? t('Editor') : t('Viewer')}
                        </Badge>
                      )}
                      {!isAdmin && !isInherited && !readOnly && (
                    <div className="flex gap-2 items-center">
                        <Dropdown
                          options={permissionOptions}
                          onSelect={(option) => {
                            handleAccessChange(personId, option.value as 'edit' | 'view');
                          }}
                          value={collab.access}
                          placeholder={t('Access')}
                          width="w-20"
                          thinner
                        />
                      {/* The owner is never removable, and a read-only viewer
                          removes nobody. Hidden rather than disabled: an
                          inert × invites a click that can never work. */}
                      {!isAdmin && !readOnly && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="small"
                          onClick={() => handleRemove(personId)}
                          ariaLabel={t('Remove collaborator')}
                        >
                          <PiXLight className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
            </div>
          </>
        )}

      {/* Current user lead status - only show when lead selection is enabled */}
      {enableLeadSelection && !loading && (
        <div className="mt-3 p-3 bg-info-50 dark:bg-info-900/20 rounded-lg border border-info-200 dark:border-info-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="tertiary"
                size="small"
                onClick={handleToggleCurrentUserLead}
                ariaLabel={currentUserIsLead ? t('Unset yourself as lead') : t('Set yourself as lead')}
                className={`p-1 ${currentUserIsLead ? 'text-info-500' : 'text-gray-400 hover:text-info-500'}`}
              >
                {currentUserIsLead ? <PiStarFill className="h-4 w-4" /> : <PiStar className="h-4 w-4" />}
              </Button>
              <ProfileImage
                profilePicture={userInfo?.profile_picture || userInfo?.ProfilePicture || null}
                email={userInfo?.Email || ''}
                initials={userInfo?.FirstName && userInfo?.LastName ? `${userInfo.FirstName.charAt(0)}${userInfo.LastName.charAt(0)}`.toUpperCase() : ''}
                firstName={userInfo?.FirstName || ''}
                lastName={userInfo?.LastName || ''}
                size="h-6 w-6"
                className="flex-shrink-0"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                {t('You are the lead')} {currentUserIsLead ? '' : t('(not set)')}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('If no collaborator is set as lead, you will be the lead by default.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CollaboratorSelector;
